import { useEffect, useMemo, useState, useRef } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { MobileActionBar } from "../components/MobileActionBar";
import {
  MapPin, Bookmark, Share2, Lock, CheckCircle2, FileText, ShieldCheck,
  Loader2, Trophy, Star, AlertCircle, MessageSquare, Send, Download, Trash2, Upload,
} from "lucide-react";
import { Badge, Feature } from "../components/ui/kit";
import { projects as mockProjects, peso } from "../data/mock";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SaveButton } from "../components/SaveButton";
import { copyShareLink } from "../lib/actions";

const TABS = ["Project Details", "Requirements", "Attachments", "Bidding", "Q&A"];
const CAN_BID = ["offer_service", "promote_company"];

type ViewModel = {
  id: string; title: string; category: string; location: string; budget: number;
  budgetType: string; deadline: string; bids: number; owner: string; ownerSince: string;
  duration: string; paymentTerms: string; description: string; status: string;
  scope: string[]; requirements: string[]; licenses: string[]; attachments: number;
  timeline: { label: string; date: string }[]; ownerUserId: number | null;
};

const asArr = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : []);

function buildModel(id: string, apiItem: any | null): ViewModel {
  const mock = mockProjects.find((x) => x.id === id) || null;
  const detail = (apiItem?.detail && typeof apiItem.detail === "object") ? apiItem.detail : {};
  const pick = <T,>(a: T | undefined | null, b: T | undefined | null, d: T): T =>
    (a ?? b ?? d) as T;
  return {
    id: apiItem?.id || mock?.id || id,
    title: pick(apiItem?.title, mock?.title, "Project"),
    category: pick(apiItem?.category, mock?.category, "General"),
    location: pick(apiItem?.location, mock?.location, "Philippines"),
    budget: Number(pick(apiItem?.budget, mock?.budget, 0)),
    budgetType: pick(apiItem?.budgetType, mock?.budgetType, "Estimated"),
    deadline: pick(apiItem?.deadline, mock?.deadline, "Open"),
    bids: Number(pick(apiItem?.bids, mock?.bids, 0)),
    owner: pick(apiItem?.owner, mock?.owner, "KontrataPH Member"),
    ownerSince: pick(detail.ownerSince, mock?.ownerSince, "2025"),
    duration: pick(detail.duration || null, mock?.duration, "To be discussed"),
    paymentTerms: pick(detail.paymentTerms || null, mock?.paymentTerms, "Milestone-based"),
    description: pick(detail.description || null, mock?.description, "No description provided."),
    status: pick(apiItem?.status, "open", "open"),
    scope: asArr(detail.scope).length ? asArr(detail.scope) : (mock?.scope || []),
    requirements: asArr(detail.requirements).length ? asArr(detail.requirements) : (mock?.requirements || []),
    licenses: asArr(detail.licenses).length ? asArr(detail.licenses) : (mock?.licenses || []),
    attachments: mock?.attachments ?? 0,
    timeline: mock?.timeline || [
      { label: "Project Posted", date: apiItem?.createdAt ? new Date(apiItem.createdAt).toLocaleDateString() : "—" },
      { label: "Open for Bidding", date: `Until ${pick(apiItem?.deadline, mock?.deadline, "the deadline")}` },
      { label: "Shortlisting", date: "After the deadline" },
      { label: "Awarding", date: "TBD" },
    ],
    ownerUserId: apiItem?.ownerUserId ?? null,
  };
}

const money = (s: string) => Number(String(s).replace(/[^\d]/g, "")) || 0;

export default function ProjectDetail() {
  const [, params] = useRoute("/project/:id");
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const id = params?.id || "";

  const [item, setItem] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Project Details");
  const [shareMsg, setShareMsg] = useState<string | null>(null);

  // bids state
  const [bidData, setBidData] = useState<Awaited<ReturnType<typeof api.getBids>>>(null as any);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [negBidId, setNegBidId] = useState<string | null>(null); // owner: which bid's thread is open

  // attachments + Q&A (Increment 6)
  const [attachments, setAttachments] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);

  // submit form
  const [form, setForm] = useState({ bidderName: "", price: "", durationText: "", terms: "", company: false });

  const p = useMemo(() => buildModel(id, item), [id, item]);
  const canBid = !!user && CAN_BID.some((c) => user.capabilities?.includes(c));
  const isProjectOwner = !!user && p.ownerUserId != null && p.ownerUserId === user.id;

  async function loadExtras() {
    try {
      const [a, q] = await Promise.all([api.listAttachments(id), api.listQuestions(id)]);
      setAttachments(a?.attachments || []);
      setQuestions(q?.questions || []);
    } catch { /* keep */ }
  }
  useEffect(() => { loadExtras(); /* eslint-disable-next-line */ }, [id]);

  async function loadBids() {
    if (!user) { setBidData(null as any); return; }
    try { setBidData(await api.getBids(id)); } catch { /* keep */ }
  }

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getProject(id)
      .then((it) => { if (alive) setItem(it); })
      .catch(() => { if (alive) setItem(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { loadBids(); /* eslint-disable-next-line */ }, [id, user?.id]);

  const isOwner = !!bidData?.isOwner;
  const myBid = (bidData as any)?.myBid || null;
  const awarded = p.status === "awarded" || bidData?.status === "awarded";
  const bidRef = useRef<HTMLDivElement>(null);

  async function submitBid(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const price = money(form.price);
    if (price <= 0) { setErr("Please enter a valid bid price."); return; }
    setBusy(true);
    try {
      await api.submitBid(id, {
        price,
        durationText: form.durationText || undefined,
        terms: form.terms || undefined,
        company: form.company,
        bidderName: form.bidderName || undefined,
      });
      // refresh item (for the live public count) + sealed bid state
      const it = await api.getProject(id);
      setItem(it);
      await loadBids();
    } catch (e: any) {
      setErr(e?.message || "Could not submit your bid.");
    } finally {
      setBusy(false);
    }
  }

  async function shortlist(bidId: string) {
    setBusy(true);
    try { await api.shortlistBid(id, bidId); await loadBids(); } catch (e: any) { setErr(e?.message || "Action failed."); } finally { setBusy(false); }
  }
  async function award(bidId: string) {
    setBusy(true);
    try {
      await api.awardBid(id, bidId);
      const it = await api.getProject(id); setItem(it);
      await loadBids();
    } catch (e: any) { setErr(e?.message || "Could not award this bid."); } finally { setBusy(false); }
  }
  async function removeAttachment(attId: string) {
    try { await api.deleteAttachment(id, attId); await loadExtras(); } catch { /* ignore */ }
  }

  if (loading) {
    return (
      <div className="container-x py-24 text-center text-mut">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-navy" />
        Loading project…
      </div>
    );
  }

  const statusChip = awarded
    ? <span className="chip bg-brand-gold/15 text-[#8a5a00] mb-2">Awarded</span>
    : <span className="chip bg-brand-green/10 text-brand-greendark mb-2">Open for Bidding</span>;

  return (
    <div className="container-x py-6 pb-24 lg:pb-6">
      <div className="text-sm text-mut mb-4">
        <Link href="/marketplace?tab=Projects" className="hover:text-navy">Projects &amp; Bids</Link> / {p.category} / Project Detail
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-4">
        <div className="min-w-0">
          {/* Header */}
          <div className="card p-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                {statusChip}
                <h1 className="text-2xl font-extrabold text-navy">{p.title}</h1>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-mut">
                  <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{p.location}</span>
                  <span>Category: {p.category}</span>
                  <span>Posted by {p.owner} · member since {p.ownerSince}</span>
                </div>
              </div>
              <div className="flex gap-2">
                {user && p.ownerUserId != null && p.ownerUserId !== user.id && (
                  <button onClick={() => navigate(`/messages?to=${p.ownerUserId}&type=project&id=${encodeURIComponent(id)}&name=${encodeURIComponent(p.owner)}`)} className="btn-outline h-9 px-3 text-xs"><MessageSquare className="w-4 h-4" />Message</button>
                )}
                {user
                  ? <SaveButton kind="project" refId={id} label="Watchlist" className="h-9 px-3 text-xs" />
                  : <button onClick={() => navigate("/login")} className="btn-outline h-9 px-3 text-xs"><Bookmark className="w-4 h-4" />Watchlist</button>}
                <button onClick={async () => { const okc = await copyShareLink(); setShareMsg(okc ? "Link copied!" : "Copy failed"); setTimeout(() => setShareMsg(null), 2000); }} className="btn-outline h-9 px-3 text-xs"><Share2 className="w-4 h-4" />{shareMsg || "Share"}</button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="card mt-4">
            <div className="px-4 border-b border-line flex gap-1 overflow-x-auto">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-brand-red text-navy" : "border-transparent text-mut hover:text-navy"}`}>
                  {t}{t === "Bidding" ? ` (${p.bids})` : ""}{t === "Attachments" ? ` (${attachments.length})` : ""}{t === "Q&A" ? ` (${questions.length})` : ""}
                </button>
              ))}
            </div>
            <div className="p-6">
              {tab === "Project Details" && (
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-navy mb-2">Project Description</h3>
                    <p className="text-sm text-mut mb-3 whitespace-pre-wrap">{p.description}</p>
                    {p.requirements.length > 0 && (
                      <ul className="space-y-1.5">{p.requirements.map((r) => <li key={r} className="text-sm text-mut flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-brand-gold" />{r}</li>)}</ul>
                    )}
                  </div>
                  <div>
                    <h3 className="font-bold text-navy mb-2">Scope of Work</h3>
                    {p.scope.length > 0
                      ? <ul className="space-y-1.5">{p.scope.map((s) => <Feature key={s}>{s}</Feature>)}</ul>
                      : <p className="text-sm text-mut">Scope to be discussed with shortlisted bidders.</p>}
                  </div>
                </div>
              )}
              {tab === "Requirements" && (
                <div className="grid sm:grid-cols-2 gap-6">
                  <div><h3 className="font-bold text-navy mb-2">Required Qualifications</h3>
                    {p.requirements.length ? <ul className="space-y-1.5">{p.requirements.map((r) => <Feature key={r}>{r}</Feature>)}</ul> : <p className="text-sm text-mut">None specified.</p>}
                  </div>
                  <div><h3 className="font-bold text-navy mb-2">Required Licenses / Documents</h3>
                    {p.licenses.length ? <ul className="space-y-1.5">{p.licenses.map((l) => <li key={l} className="text-sm text-mut flex items-center gap-2"><FileText className="w-4 h-4 text-navy shrink-0" />{l}</li>)}</ul> : <p className="text-sm text-mut">None specified.</p>}
                  </div>
                </div>
              )}
              {tab === "Attachments" && (
                <div className="space-y-3">
                  {isProjectOwner && <AttachmentUploader projectId={id} onUploaded={loadExtras} />}
                  {attachments.length === 0 ? (
                    <p className="text-sm text-mut">No attachments yet.{isProjectOwner ? " Upload plans, permits, or drawings above." : ""}</p>
                  ) : (
                    <ul className="divide-y divide-line">
                      {attachments.map((a) => (
                        <li key={a.id} className="flex items-center justify-between py-2 gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-5 h-5 text-navy shrink-0" />
                            <div className="min-w-0">
                              <a href={api.attachmentDownloadUrl(id, a.id)} target="_blank" rel="noreferrer" className="text-sm font-medium text-navy hover:text-brand-red truncate block">{a.filename}</a>
                              <div className="text-xs text-mut">{fmtSize(a.size)}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <a href={api.attachmentDownloadUrl(id, a.id)} target="_blank" rel="noreferrer" className="btn-outline h-8 px-2.5 text-xs"><Download className="w-3.5 h-3.5" />Download</a>
                            {isProjectOwner && <button onClick={() => removeAttachment(a.id)} className="btn-outline h-8 px-2 text-xs text-brand-red" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {tab === "Bidding" && (
                <div className="text-center py-6">
                  <Lock className="w-8 h-8 text-navy mx-auto mb-2" />
                  <h3 className="font-bold text-navy">Sealed Bidding</h3>
                  <p className="text-sm text-mut max-w-md mx-auto mt-1">
                    Bids are sealed — competitors cannot see each other&apos;s prices. {p.bids} bid{p.bids === 1 ? "" : "s"} received so far.
                    {awarded ? " This project has been awarded." : " The owner shortlists after the deadline."}
                  </p>
                </div>
              )}
              {tab === "Q&A" && (
                <div className="space-y-3">
                  {questions.length === 0 && <p className="text-sm text-mut">No questions yet.{user && !isProjectOwner ? " Be the first to ask." : ""}</p>}
                  {questions.map((q) => (
                    <div key={q.id} className="border border-line rounded-lg p-3">
                      <div className="font-medium text-navy text-sm">Q: {q.question}</div>
                      <div className="text-[11px] text-mut mt-0.5">asked by {q.askedByName}</div>
                      {q.answer ? (
                        <div className="text-sm text-mut mt-2 border-l-2 border-brand-green pl-2">A: {q.answer}</div>
                      ) : isProjectOwner ? (
                        <AnswerForm projectId={id} qid={q.id} onAnswered={loadExtras} />
                      ) : (
                        <div className="text-xs text-mut mt-1 italic">Awaiting the owner&apos;s answer</div>
                      )}
                    </div>
                  ))}
                  {user ? (
                    !isProjectOwner && <AskForm projectId={id} onAsked={loadExtras} />
                  ) : (
                    <div className="text-sm text-mut border-t border-line pt-3 mt-3">
                      <button onClick={() => navigate("/login")} className="text-brand-red font-semibold">Sign in</button> to ask a question.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Owner bid management */}
          {isOwner && (
            <div className="card p-6 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-navy">Bids Received ({bidData?.total ?? 0})</h3>
                {awarded && <span className="chip bg-brand-gold/15 text-[#8a5a00]">Awarded</span>}
              </div>
              {err && <div className="mb-3 text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</div>}
              {(bidData?.bids?.length ?? 0) === 0 ? (
                <p className="text-sm text-mut">No bids yet. As contractors submit sealed bids, you&apos;ll see their prices here — hidden from other bidders.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-mut border-b border-line">
                        <th className="py-2 pr-3 font-semibold">Bidder</th>
                        <th className="py-2 px-3 font-semibold">Price</th>
                        <th className="py-2 px-3 font-semibold">Duration</th>
                        <th className="py-2 px-3 font-semibold">Status</th>
                        <th className="py-2 pl-3 font-semibold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bidData!.bids.map((b: any) => (
                        <tr key={b.id} className={`border-b border-line/60 ${b.status === "awarded" ? "bg-brand-gold/5" : ""}`}>
                          <td className="py-2 pr-3">
                            <div className="font-medium text-navy">{b.bidderName}</div>
                            {b.company && <div className="text-[11px] text-mut">Company</div>}
                            {b.terms && <div className="text-[11px] text-mut max-w-[220px] truncate" title={b.terms}>{b.terms}</div>}
                          </td>
                          <td className="py-2 px-3 font-semibold text-navy">
                            {peso(b.status === "awarded" && b.finalPrice != null ? b.finalPrice : (b.standingPrice ?? b.price))}
                            {b.inNegotiation && b.standingPrice !== b.price && (
                              <div className="text-[11px] font-normal text-mut">was {peso(b.price)}</div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-mut">{b.durationText || "—"}</td>
                          <td className="py-2 px-3">
                            {b.status === "awarded" && <span className="chip bg-brand-gold/15 text-[#8a5a00]">Awarded</span>}
                            {b.status === "in_negotiation" && <span className="chip bg-brand-gold/10 text-[#8a5a00]">Negotiating</span>}
                            {b.status === "shortlisted" && <span className="chip bg-brand-green/10 text-brand-greendark">Shortlisted</span>}
                            {b.status === "submitted" && <span className="chip bg-navy-50 text-navy">Submitted</span>}
                            {b.status === "rejected" && <span className="chip bg-line text-mut">Not selected</span>}
                          </td>
                          <td className="py-2 pl-3 text-right whitespace-nowrap">
                            {!awarded && (
                              <>
                                <button disabled={busy} onClick={() => shortlist(b.id)} className="btn-outline h-8 px-2.5 text-xs mr-1">
                                  <Star className="w-3.5 h-3.5" />{b.status === "shortlisted" ? "Unshortlist" : "Shortlist"}
                                </button>
                                <button disabled={busy} onClick={() => setNegBidId(negBidId === b.id ? null : b.id)} className="btn-outline h-8 px-2.5 text-xs mr-1">
                                  <MessageSquare className="w-3.5 h-3.5" />{negBidId === b.id ? "Close" : "Negotiate"}
                                </button>
                                <button disabled={busy} onClick={() => award(b.id)} className="btn-red h-8 px-2.5 text-xs">
                                  <Trophy className="w-3.5 h-3.5" />Award
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {negBidId && !awarded && (
                <div className="mt-4 border-t border-line pt-4">
                  <NegotiationThread
                    projectId={id}
                    bidId={negBidId}
                    projectOpen={!awarded}
                    onAward={() => { award(negBidId); setNegBidId(null); }}
                    onChanged={loadBids}
                  />
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="card p-6 mt-4">
            <h3 className="font-bold text-navy mb-4">Project Timeline</h3>
            <ol className="relative border-l-2 border-line ml-2 space-y-4">
              {p.timeline.map((t, i) => (
                <li key={t.label} className="ml-4">
                  <span className={`absolute -left-[9px] w-4 h-4 rounded-full ${i === 1 ? "bg-brand-red" : "bg-navy-50 border-2 border-navy-500"}`} />
                  <div className="text-sm font-semibold text-navy">{t.label}</div>
                  <div className="text-xs text-mut">{t.date}</div>
                </li>
              ))}
            </ol>
          </div>

          {/* Sealed-bid process */}
          <div className="card p-6 mt-4">
            <h3 className="font-bold text-navy mb-4">Sealed-Bid Process</h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {["Submitted", "Shortlisted", "Negotiation", "Awarded", "Contract Signed"].map((s, i) => (
                <div key={s} className="text-center"><div className="w-9 h-9 rounded-full bg-navy-50 text-navy font-bold grid place-items-center mx-auto mb-1">{i + 1}</div><div className="text-xs font-medium text-navy">{s}</div></div>
              ))}
            </div>
          </div>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-4 min-w-0">
          <div className="card p-5">
            <h3 className="font-bold text-navy mb-3">Bid Summary</h3>
            <dl className="text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-mut">Status</dt><dd className="font-semibold text-navy capitalize">{awarded ? "Awarded" : "Open"}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Open until</dt><dd className="font-semibold text-navy">{p.deadline}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">{p.budgetType} Budget</dt><dd className="font-semibold text-navy">{peso(p.budget)}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Bids Received</dt><dd className="font-semibold text-navy">{p.bids}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Duration</dt><dd className="font-semibold text-navy">{p.duration}</dd></div>
              <div className="flex justify-between"><dt className="text-mut">Payment Terms</dt><dd className="font-semibold text-navy">{p.paymentTerms}</dd></div>
            </dl>
          </div>

          {/* Submit sealed bid — hidden for the owner */}
          {!isOwner && (
            <div ref={bidRef} className="card p-5">
              <div className="flex items-center gap-2 mb-1"><Lock className="w-4 h-4 text-brand-green" /><h3 className="font-bold text-navy">Submit Your Sealed Bid</h3></div>
              <p className="text-xs text-mut mb-4">Your price is hidden from other bidders.</p>

              {awarded ? (
                <div className="text-center py-6">
                  <Trophy className="w-10 h-10 text-brand-gold mx-auto mb-2" />
                  <div className="font-bold text-navy">Bidding closed</div>
                  <p className="text-xs text-mut mt-1">
                    {myBid?.status === "awarded" ? "Congratulations — your bid was awarded." : "This project has been awarded to another bidder."}
                  </p>
                </div>
              ) : !user ? (
                <div className="text-center py-4">
                  <p className="text-sm text-mut mb-3">Sign in to submit a sealed bid.</p>
                  <button onClick={() => navigate("/login")} className="btn-red w-full">Sign in to bid</button>
                </div>
              ) : !canBid ? (
                <div className="text-center py-4">
                  <AlertCircle className="w-7 h-7 text-brand-gold mx-auto mb-2" />
                  <p className="text-sm text-mut mb-3">Bidding needs the “Offer a Service” or “Promote a Company” activity enabled on your account.</p>
                  <button onClick={() => navigate("/dashboard")} className="btn-outline w-full">Manage activities</button>
                </div>
              ) : myBid ? (
                <div>
                  <div className="rounded-lg border border-brand-green/30 bg-brand-green/5 p-3 mb-3 text-center">
                    <CheckCircle2 className="w-8 h-8 text-brand-green mx-auto mb-1" />
                    <div className="font-bold text-navy text-sm">Your sealed bid is in</div>
                    <div className="text-lg font-extrabold text-navy mt-1">{peso(myBid.standingPrice ?? myBid.price)}</div>
                    <div className="text-xs text-mut">
                      {myBid.durationText ? `${myBid.durationText} · ` : ""}
                      {myBid.status === "in_negotiation" ? "In negotiation" : myBid.status === "shortlisted" ? "Shortlisted by owner" : "Submitted"}
                    </div>
                    {myBid.inNegotiation && myBid.standingPrice !== myBid.price && (
                      <div className="text-[11px] text-mut mt-0.5">original bid {peso(myBid.price)}</div>
                    )}
                  </div>

                  {myBid.inNegotiation ? (
                    <div className="border-t border-line pt-3">
                      <NegotiationThread
                        projectId={id}
                        bidId={myBid.id}
                        projectOpen={!awarded}
                        onChanged={loadBids}
                      />
                    </div>
                  ) : (
                    <>
                      <p className="text-[11px] text-mut mb-2 text-center">You can revise your bid before the deadline.</p>
                      {err && <div className="mb-2 text-xs text-brand-red text-center">{err}</div>}
                      <details>
                        <summary className="text-xs font-semibold text-navy cursor-pointer select-none">Revise my bid</summary>
                        <BidForm form={form} setForm={setForm} busy={busy} onSubmit={submitBid} submitLabel="Update Sealed Bid" />
                      </details>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {err && <div className="mb-2 text-xs text-brand-red text-center flex items-center gap-1 justify-center"><AlertCircle className="w-3.5 h-3.5" />{err}</div>}
                  <BidForm form={form} setForm={setForm} busy={busy} onSubmit={submitBid} submitLabel="Submit Sealed Bid" />
                </>
              )}
            </div>
          )}

          <div className="card p-5"><h3 className="font-bold text-navy mb-2">Required to Bid</h3>
            {p.licenses.length ? <div className="flex flex-wrap gap-1.5">{p.licenses.map((l) => <Badge key={l}>{l}</Badge>)}</div> : <p className="text-sm text-mut">No specific licenses required.</p>}
          </div>
        </div>
      </div>

      {!isOwner && !awarded && (
        <MobileActionBar>
          <div className="shrink-0">
            <div className="text-[11px] text-mut leading-none">{p.budgetType} Budget</div>
            <div className="text-base font-extrabold text-navy leading-tight">{peso(p.budget)}</div>
          </div>
          <button type="button" onClick={() => bidRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="btn-red flex-1"><Lock className="w-4 h-4" />{myBid ? "View my bid" : "Submit a Bid"}</button>
        </MobileActionBar>
      )}
    </div>
  );
}

function BidForm({
  form, setForm, busy, onSubmit, submitLabel,
}: {
  form: { bidderName: string; price: string; durationText: string; terms: string; company: boolean };
  setForm: (f: any) => void;
  busy: boolean;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel: string;
}) {
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  return (
    <form onSubmit={onSubmit} className="space-y-3 mt-3">
      <div><label className="label">Company / Bidder</label><input className="field" value={form.bidderName} onChange={(e) => set("bidderName", e.target.value)} placeholder="Your company name" /></div>
      <div><label className="label">Proposed Price (PHP)</label><input className="field" inputMode="numeric" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="e.g. 5,800,000" /></div>
      <div><label className="label">Proposed Completion Period</label><input className="field" value={form.durationText} onChange={(e) => set("durationText", e.target.value)} placeholder="e.g. 150 days" /></div>
      <div><label className="label">Terms &amp; Exclusions</label><textarea className="field h-20 py-2" value={form.terms} onChange={(e) => set("terms", e.target.value)} placeholder="Notable inclusions/exclusions…" /></div>
      <label className="flex items-center gap-2 text-xs text-mut"><input type="checkbox" checked={form.company} onChange={(e) => set("company", e.target.checked)} />Bidding as a registered company</label>
      <button disabled={busy} className="btn-red w-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}{submitLabel}</button>
      <p className="text-[11px] text-mut flex items-center gap-1 justify-center"><ShieldCheck className="w-3 h-3" />Protected by escrow on award</p>
    </form>
  );
}

const fmtSize = (n: number) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

function AttachmentUploader({ projectId, onUploaded }: { projectId: string; onUploaded: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null); setBusy(true);
    try {
      await api.uploadAttachment(projectId, file);
      onUploaded();
    } catch (e: any) { setErr(e?.message || "Upload failed."); }
    finally { setBusy(false); }
  }
  return (
    <div className="border-2 border-dashed border-line rounded-lg p-4 text-center">
      <input id="att-file" type="file" className="hidden" onChange={onPick} disabled={busy} />
      <label htmlFor="att-file" className={`btn-navy inline-flex cursor-pointer ${busy ? "opacity-70 pointer-events-none" : ""}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{busy ? "Uploading…" : "Upload file"}
      </label>
      <p className="text-xs text-mut mt-2">PDF, images, or Office docs · up to 25 MB</p>
      {err && <p className="text-xs text-brand-red mt-1">{err}</p>}
    </div>
  );
}

function AskForm({ projectId, onAsked }: { projectId: string; onAsked: () => void }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (q.trim().length < 3) { setErr("Please enter a question."); return; }
    setBusy(true); setErr(null);
    try { await api.askQuestion(projectId, q.trim()); setQ(""); onAsked(); }
    catch (e: any) { setErr(e?.message || "Could not post your question."); }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="border-t border-line pt-3 mt-3">
      <label className="label">Ask a question</label>
      <div className="flex gap-2">
        <input className="field" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask the project owner…" />
        <button disabled={busy} className="btn-navy px-3">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>
      {err && <p className="text-xs text-brand-red mt-1">{err}</p>}
    </form>
  );
}

function AnswerForm({ projectId, qid, onAnswered }: { projectId: string; qid: string; onAnswered: () => void }) {
  const [a, setA] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!a.trim()) return;
    setBusy(true);
    try { await api.answerQuestion(projectId, qid, a.trim()); setA(""); onAnswered(); }
    catch { /* ignore */ }
    finally { setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="mt-2 flex gap-2">
      <input className="field h-9" value={a} onChange={(e) => setA(e.target.value)} placeholder="Answer this question…" />
      <button disabled={busy} className="btn-red h-9 px-3 text-xs">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Answer"}</button>
    </form>
  );
}

function Bubble({ side, me, label, price, text }: {
  side: "owner" | "bidder"; me: boolean; label: string; price: number; text?: string;
}) {
  return (
    <div className={`flex ${me ? "justify-end" : "justify-start"}`} data-side={side}>
      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs ${me ? "bg-navy text-white" : "bg-navy-50 text-navy"}`}>
        <div className="flex items-center justify-between gap-3">
          <span className={`font-semibold ${me ? "text-white/80" : "text-navy"}`}>{label}</span>
          <span className="font-bold">{peso(price)}</span>
        </div>
        {text && <div className={`mt-0.5 ${me ? "text-white/85" : "text-mut"}`}>{text}</div>}
      </div>
    </div>
  );
}

function NegotiationThread({
  projectId, bidId, projectOpen, onAward, onChanged,
}: {
  projectId: string; bidId: string; projectOpen: boolean;
  onAward?: () => void; onChanged?: () => void;
}) {
  const [thread, setThread] = useState<Awaited<ReturnType<typeof api.getNegotiation>>>(null as any);
  const [price, setPrice] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try { setThread(await api.getNegotiation(projectId, bidId)); }
    catch (e: any) { setErr(e?.message || "Could not load negotiation."); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, bidId]);

  if (!thread) return <div className="text-xs text-mut flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading negotiation…</div>;
  const role = thread.role;
  const canRespond = projectOpen && (role === "owner" || thread.bidStatus === "in_negotiation");

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const p = money(price);
    if (p <= 0) { setErr("Enter a valid counter price."); return; }
    setBusy(true);
    try {
      await api.postCounter(projectId, bidId, { price: p, message: msg || undefined });
      setPrice(""); setMsg("");
      await load();
      onChanged?.();
    } catch (e: any) { setErr(e?.message || "Could not send counter-offer."); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold text-navy flex items-center gap-1.5">
          <MessageSquare className="w-4 h-4" />Negotiation{role === "owner" ? ` · ${thread.bidderName}` : ""}
        </div>
        <div className="text-xs text-mut">Standing: <span className="font-semibold text-navy">{peso(thread.standingPrice)}</span></div>
      </div>

      <div className="space-y-2 mb-3 max-h-56 overflow-y-auto pr-1">
        <Bubble side="bidder" me={role === "bidder"} label={role === "owner" ? thread.bidderName : "You"} price={thread.openingPrice} text="Opening sealed bid" />
        {thread.offers.map((o) => (
          <Bubble
            key={o.id}
            side={o.byRole as "owner" | "bidder"}
            me={o.byRole === role}
            label={o.byRole === "owner" ? "Owner" : (role === "owner" ? thread.bidderName : "You")}
            price={o.price}
            text={o.message || undefined}
          />
        ))}
      </div>

      {err && <div className="text-xs text-brand-red mb-2">{err}</div>}

      {canRespond ? (
        <form onSubmit={send} className="space-y-2">
          <input className="field h-9" inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={role === "owner" ? "Counter price (PHP)" : "Your revised price (PHP)"} />
          <input className="field h-9" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message (optional)" />
          <div className="flex gap-2">
            <button disabled={busy} className="btn-outline h-9 px-3 text-xs flex-1">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}{role === "owner" ? "Send counter" : "Send response"}</button>
            {role === "owner" && onAward && (
              <button type="button" disabled={busy} onClick={onAward} className="btn-red h-9 px-3 text-xs"><Trophy className="w-3.5 h-3.5" />Award {peso(thread.standingPrice)}</button>
            )}
          </div>
        </form>
      ) : (
        <div className="text-xs text-mut">{projectOpen ? "Waiting for the owner to make a counter-offer." : "Bidding is closed."}</div>
      )}
    </div>
  );
}
